import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { defaultFeaturePath, featureLabel } from '../utils/userFeatureAccess';

function JoinFeatureAccess() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, refreshUserProfile } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`/api/feature-invites/${encodeURIComponent(code)}/preview`);
        const data = await res.json();
        if (!res.ok) throw new Error(t('featureAccess.inviteInvalid'));
        if (!cancelled) setInvite(data);
      } catch (e) {
        if (!cancelled) setError(t('featureAccess.inviteInvalid'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, t]);

  const handleContinue = async () => {
    if (!isAuthenticated) {
      navigate(`/register?access=${encodeURIComponent(code)}`);
      return;
    }

    setAccepting(true);
    setError('');
    try {
      const res = await apiClient.post('/api/feature-access/accept', { invite_code: code });
      const data = await res.json();
      if (!res.ok) throw new Error(t('featureAccess.acceptFailed'));
      if (data.token) localStorage.setItem('token', data.token);
      await refreshUserProfile?.();
      navigate(data.default_path || defaultFeaturePath({ access_mode: 'feature_trial', feature_access: data.features?.map((feature) => ({ feature })) || [] }));
    } catch (e) {
      setError(t('featureAccess.acceptFailed'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-canvas px-4">
        <p className="text-ink-muted">{t('featureAccess.loading')}</p>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-canvas px-4">
        <div className="max-w-md w-full rounded-lg border border-red-200 bg-surface p-6 text-center">
          <p className="text-red-700">{error}</p>
          <Link to="/login" className="mt-4 inline-block text-link hover:text-link-hover">
            {t('featureAccess.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[50vh] bg-canvas py-10 px-4">
      <div className="max-w-lg mx-auto rounded-lg border border-edge bg-surface p-6 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-wide text-link">
          {t('featureAccess.eyebrow')}
        </p>
        <h1 className="mt-2 text-2xl font-bold text-ink">{t('featureAccess.title')}</h1>
        {invite?.label ? <p className="mt-2 text-ink-muted">{invite.label}</p> : null}
        <ul className="mt-4 space-y-2">
          {(invite?.features || []).map((key) => (
            <li key={key} className="rounded-md bg-accent/25 px-3 py-2 text-sm text-blue-900">
              {featureLabel(t, key)}
            </li>
          ))}
        </ul>
        {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}
        <button
          type="button"
          onClick={handleContinue}
          disabled={accepting}
          className="mt-6 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-60"
        >
          {accepting
            ? t('featureAccess.accepting')
            : isAuthenticated
              ? t('featureAccess.acceptAndContinue')
              : t('featureAccess.registerToContinue')}
        </button>
        <p className="mt-3 text-center text-xs text-ink-subtle">
          {invite?.join_url}
        </p>
      </div>
    </div>
  );
}

export default JoinFeatureAccess;
